import React from "react";
import styled from "styled-components";
import { Link } from "@inertiajs/react";
import {
  brand_orange,
  brand_orange_deep,
  brand_yellow,
  brand_blue,
  brand_success,
} from "../../constants/styles";
import PagedCarousel from "../Carousel";
import SectionShell, {
  SectionTitle,
  SectionSubtitle,
  SectionGroup,
} from "../SectionShell";
import usePagedSection from "../usePagedSection";
import { Skeleton } from "../Skeleton";
import SeeAllSectionLink from "../SeeAllSectionLink";
import { apiGet } from "../../api";
import { LoggedInUserContext } from "../../providers/ProvideLoggedInUser";

// Replaces the old "Rekaman" section, which hardcoded two groups (tag-curated
// Courses and past Webinars). Groups now come from the `lesson_learning_names`
// vocabulary — "Nama Materi Belajar Pajak" — so an admin curates the homepage
// from /admin/lesson_learning_names instead of from a deploy. Recordings still
// appear, because that vocabulary already contains "Rekaman Webinar" and
// "Rekaman Live Online Classes …".
//
// Cards are exported and shared with the /video-pembelajaran catalog so the two
// surfaces can never drift apart.

const SUBGRID_ROWS = 5;

// Two placeholder groups while the payload loads. Named keys rather than array
// indices so the list is stable without tripping no-array-index-key.
const SKELETON_GROUP_KEYS = ["skeleton-group-1", "skeleton-group-2"];

// The card IS the link. It has to BE the grid item rather than wrap one, or the
// subgrid chain breaks and rows stop aligning across the row of cards. Nothing
// inside is interactive, so there is no nested-interactive hazard.
const GridCard = styled(Link)`
  display: grid;
  grid-row: span ${SUBGRID_ROWS};
  grid-template-rows: subgrid;
  row-gap: 10px;
  background: #f9fafb;
  border-radius: 16px;
  padding: 14px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  cursor: pointer;
  text-decoration: none;
  color: inherit;
  transition:
    transform 200ms ease,
    box-shadow 200ms ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.1);
    color: inherit;
    text-decoration: none;
  }

  &:focus-visible {
    outline: 3px solid ${brand_orange};
    outline-offset: 3px;
  }

  &[data-theme="dark"],
  [data-theme="dark"] & {
    background: #17171e;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);

    &:hover {
      background: #1f2937;
      box-shadow: 0 12px 30px rgba(0, 0, 0, 0.5);
    }
  }

  /* Graceful fallback where subgrid is unsupported: a normal stacked card. */
  @supports not (grid-template-rows: subgrid) {
    grid-row: auto;
    grid-template-rows: none;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
`;

const Thumbnail = styled.div`
  position: relative;
  aspect-ratio: 16 / 10;
  border-radius: 12px;
  overflow: hidden;
  background: linear-gradient(135deg, #fde3cf 0%, #f7c59f 100%);
  display: flex;
  align-items: center;
  justify-content: center;

  [data-theme="dark"] & {
    background: linear-gradient(135deg, #2a2118 0%, #3b2a1a 100%);
  }
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

// A lesson has only its own banner_image — there is no course or curriculum
// banner to fall back to — so an initial stands in rather than a broken frame.
const ThumbnailPlaceholder = styled.span`
  font-size: 34px;
  font-weight: 800;
  color: ${brand_orange_deep};
  opacity: 0.5;

  [data-theme="dark"] & {
    color: ${brand_yellow};
  }
`;

const DurationBadge = styled.span`
  position: absolute;
  right: 8px;
  bottom: 8px;
  padding: 2px 7px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.75);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`;

// A div, not a heading: the enclosing SectionGroup already renders an <h3> for
// the Nama Materi Belajar, so an <h3> here would flatten the outline into a run
// of same-level headings. Matches SertifikasiCarousel and RekamanCarousel, the
// other grouped sections. (WebinarCarousel uses an h3 because it has no groups.)
const CardTitle = styled.div`
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.4;
  color: ${brand_orange_deep};

  /* Two lines, then ellipsis, so a long title cannot push the rows below it out
   * of alignment with neighbouring cards.
   */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  [data-theme="dark"] & {
    color: ${brand_yellow};
  }
`;

const CardMeta = styled.div`
  font-size: 12px;
  line-height: 1.5;
  color: #6b7280;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;

  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: flex-start;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  background: ${(props) => props.$bg || "#eef2ff"};
  color: ${(props) => props.$fg || brand_blue};
`;

const ProgressTrack = styled.div`
  height: 4px;
  border-radius: 999px;
  background: #e5e7eb;
  overflow: hidden;
  align-self: end;

  [data-theme="dark"] & {
    background: #374151;
  }
`;

const ProgressFill = styled.div`
  height: 100%;
  border-radius: 999px;
  background: ${brand_success};
  width: ${(props) => props.$pct}%;
`;

// Keeps the 5-row subgrid intact when there is nothing to show in the last row.
// Without it the rows above would shift on cards lacking progress.
const RowSpacer = styled.div`
  align-self: end;
`;

// Positional, not a props bag — it is handed straight to `renderItem` and sets
// its own key, matching WebinarGridCard.
export function LearningVideoGridCard(video) {
  const pct = Number(video.progress_percent) || 0;
  // Deduped: for a lot of lessons `category_name` is literally the same string as
  // the Nama Materi Belajar, which rendered as "PPN dan PPnBM • PPN dan PPnBM".
  const meta = [
    ...new Set([video.learning_name, video.category_name].filter(Boolean)),
  ].join(" • ");

  return (
    <GridCard
      key={video.id}
      href={video.detail_url || "#"}
      aria-label={`Tonton ${video.title}`}
    >
      <Thumbnail>
        {video.image_url ? (
          <ThumbnailImage src={video.image_url} alt="" loading="lazy" />
        ) : (
          <ThumbnailPlaceholder aria-hidden="true">
            {video.title ? video.title.charAt(0) : "V"}
          </ThumbnailPlaceholder>
        )}
        {video.duration_label && (
          <DurationBadge>{video.duration_label}</DurationBadge>
        )}
      </Thumbnail>

      <CardTitle>{video.title}</CardTitle>

      <CardMeta>{meta}</CardMeta>

      <ChipRow>
        {video.is_free ? (
          <Chip $bg="#dcfce7" $fg="#166534">
            Gratis
          </Chip>
        ) : (
          <Chip $bg="#fef3c7" $fg="#92400e">
            Langganan
          </Chip>
        )}
        {video.standalone && (
          <Chip $bg="#eef2ff" $fg={brand_blue}>
            Video Mandiri
          </Chip>
        )}
      </ChipRow>

      {pct > 0 ? (
        <ProgressTrack
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${pct}% ditonton`}
        >
          <ProgressFill $pct={pct} />
        </ProgressTrack>
      ) : (
        <RowSpacer aria-hidden="true" />
      )}
    </GridCard>
  );
}

// Row parity with the real card is load-bearing: a skeleton with a different
// number of rows makes the grid jump when real data lands.
export function LearningVideoGridSkeleton(i) {
  return (
    <GridCard as="div" key={`sk-${i}`} aria-hidden="true">
      <Skeleton $radius="12px" style={{ aspectRatio: "16 / 10" }} />
      <Skeleton $width="85%" $height="16px" $radius="4px" />
      <Skeleton $width="60%" $height="12px" $radius="4px" />
      <Skeleton $width="40%" $height="18px" $radius="999px" />
      <Skeleton $width="100%" $height="4px" $radius="999px" />
    </GridCard>
  );
}

const GroupTitleLink = styled(Link)`
  color: inherit;
  text-decoration: none;

  &:hover {
    color: ${brand_orange_deep};
    text-decoration: underline;
  }

  [data-theme="dark"] &:hover {
    color: ${brand_yellow};
  }
`;

const GroupCount = styled.span`
  margin-left: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;

  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

// One carousel per Nama Materi Belajar. Paginated client-side from the already
// loaded array, matching SertifikasiCarousel — the payload is capped server-side
// at MAX_VIDEOS_PER_SECTION, so there is nothing more to fetch.
function VideoGroup({ group, loading: loadingOverride }) {
  const videos = group.videos || [];
  const { items, pageIdx, setPageIdx, totalPages, loading, perPage } =
    usePagedSection({ sampleItems: videos });

  // GroupTitle renders an <h3>, so the title must stay inline content — a block
  // Skeleton nested in a heading is invalid markup.
  const titleNode = loadingOverride ? (
    group.name
  ) : (
    <>
      <GroupTitleLink href={group.detail_url || "/video-pembelajaran"}>
        {group.name}
      </GroupTitleLink>
      {group.total > videos.length && (
        <GroupCount>{group.total} video</GroupCount>
      )}
    </>
  );

  return (
    <SectionGroup title={titleNode}>
      <PagedCarousel
        items={items}
        perPage={perPage}
        pageIdx={pageIdx}
        totalPages={totalPages}
        onPageChange={setPageIdx}
        loading={loadingOverride || loading}
        renderItem={LearningVideoGridCard}
        renderSkeleton={LearningVideoGridSkeleton}
        subgridRows={SUBGRID_ROWS}
        ariaLabel={group.name}
        emptyMessage="Belum ada video di materi ini."
      />
    </SectionGroup>
  );
}

function VideoGroupSkeleton() {
  return <VideoGroup group={{ name: "Memuat...", videos: [] }} loading />;
}

export default function VideoPembelajaranCarousel({
  videoPembelajaranIndexEnabled = false,
}) {
  const auth = React.use(LoggedInUserContext);
  const [groups, setGroups] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;

    apiGet("/api/v1/homepage/learning_videos")
      .then(({ parsedResponse }) => {
        if (cancelled) return;
        setGroups(
          Array.isArray(parsedResponse?.data) ? parsedResponse.data : [],
        );
      })
      .catch((err) => {
        console.warn("[VideoPembelajaranCarousel] fetch failed", err);
        if (!cancelled) setGroups([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Every group defaults to `shown_on_homepage: false`, so until an admin curates
  // there is genuinely nothing here. Render nothing at all rather than a titled
  // empty shell where the Rekaman section used to be.
  if (!loading && groups.length === 0) return null;

  const showAnonymousHint = Boolean(auth && !auth.loading && !auth.signedIn);

  return (
    <SectionShell id="video-pembelajaran">
      {/* The old section anchored at #rekaman; keep that target working for any
       * existing link into the page.
       */}
      <span id="rekaman" aria-hidden="true" />
      <SectionTitle>Video Pembelajaran</SectionTitle>
      <SectionSubtitle>
        Kumpulan video materi belajar pajak, dikelompokkan per topik. Pilih
        materi yang kamu butuhkan dan langsung tonton.
      </SectionSubtitle>

      {loading
        ? SKELETON_GROUP_KEYS.map((key) => <VideoGroupSkeleton key={key} />)
        : groups.map((group) => <VideoGroup key={group.id} group={group} />)}

      {videoPembelajaranIndexEnabled && (
        <SeeAllSectionLink
          href="/video-pembelajaran"
          label="Lihat Semua Video Pembelajaran"
          ariaLabel="Lihat semua video pembelajaran"
          hint={
            showAnonymousHint ? (
              <>
                Menampilkan sebagian materi.{" "}
                <a href="/login?redirect_after_login=%2Fvideo-pembelajaran">
                  Masuk
                </a>{" "}
                untuk melanjutkan progres belajarmu.
              </>
            ) : null
          }
        />
      )}
    </SectionShell>
  );
}
